<script setup lang="ts">
import { AppFlicker } from '@libs/ui';

import type { PeekPriceSmallHotelCardData } from '../models';

import PeekPriceHotelRooms from './PeekPriceHotelRooms.vue';

interface PeekPriceSmallHotelCardProps {
  data: PeekPriceSmallHotelCardData;
}

const props = defineProps<PeekPriceSmallHotelCardProps>();
</script>

<template>
  <div class="peek-price-small-hotel-card">
    <img
      v-if="props.data.imageUrl"
      :src="props.data.imageUrl"
      :alt="$t('a11y.hotel_image')"
      class="hotel-image"
    />
    <div v-else class="hotel-image-flicker">
      <AppFlicker height="100%" width="100%" />
    </div>

    <div class="hotel-card-content">
      <div class="time-section">
        <div v-if="props.data.dates" class="date-text">
          {{ props.data.dates }}
        </div>
        <div v-else class="date-text-flicker">
          <AppFlicker height="100%" width="spacing-64" />
        </div>
        <div v-if="props.data.nights" class="nights-text">
          {{ props.data.nights }}
        </div>
        <div v-else class="nights-text-flicker">
          <AppFlicker height="100%" width="spacing-48" />
        </div>
      </div>

      <div v-if="props.data.hotelName" class="hotel-name-text">
        {{ props.data.hotelName }}
      </div>
      <div v-else class="hotel-name-flicker">
        <AppFlicker height="100%" width="spacing-80" />
      </div>

      <div v-if="props.data.hotelDestination" class="hotel-destination-text">
        {{ props.data.hotelDestination }}
      </div>
      <div v-else class="hotel-destination-flicker">
        <AppFlicker height="100%" width="spacing-80" />
      </div>

      <PeekPriceHotelRooms v-if="props.data.rooms" :rooms="props.data.rooms" />
      <div v-else class="rooms-flicker">
        <AppFlicker height="100%" width="spacing-48" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.peek-price-small-hotel-card {
  --hotel-image-size: 60px;
  display: flex;
  gap: var(--spacing-12);

  .hotel-image {
    width: var(--hotel-image-size);
    height: var(--hotel-image-size);
    object-fit: cover;
    border-radius: var(--border-radius-8);
  }

  .hotel-image-flicker {
    width: var(--hotel-image-size);
    height: var(--hotel-image-size);
  }

  .hotel-card-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .time-section {
      display: flex;

      .date-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
      }

      .date-text-flicker {
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
        height: 15px;
      }

      .nights-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
      }

      .nights-text-flicker {
        height: 15px;
      }
    }

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
    }

    .hotel-name-flicker {
      height: 18px;
    }

    .hotel-destination-text {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }

    .hotel-destination-flicker {
      height: 15px;
    }

    .rooms-flicker {
      height: 18px;
    }
  }
}
</style>
